// bluaIQ — PR-F3A Financial Trust P0: anomalous contract amount read guards.
// PR-F4E: Decimal-readiness — the classifiers now accept number | string |
// Decimal-like inputs (see normalizeContractAmountInput below) so a FUTURE
// Contract.totalAmount Float→Decimal migration cannot silently misclassify
// every valid contract as anomalous. TODAY Contract.totalAmount is still Float
// (numbers in, identical behavior out) — this is readiness only, no schema
// change, no migration, no behavior change for existing numeric inputs.
//
// A contract amount is treated as anomalous/unverified for financial totals when:
//   (1) it does not normalize to a finite positive number (null, NaN, Infinity,
//       negative, zero, malformed/exponent string, throwing/junk toString)
//   (2) it is >= 1_000_000 (absolute threshold — far above a DFW spec-home trade)
//   (3) budgetTotal > 0 and amount > budgetTotal * ANOMALY_BUDGET_RATIO (outlier)
//
// Anomalous amounts are STILL DISPLAYED in the UI — we never hide a source-backed
// value. They are EXCLUDED from financial aggregates: Dashboard committed,
// AIA contractSum, and AI Chat committed-obligation context.
//
// Pure and side-effect-free — no Prisma, no Node.js-specific APIs.
// Safe to import from both backend routes and frontend components.

export const ANOMALY_ABSOLUTE_THRESHOLD = 1_000_000
export const ANOMALY_BUDGET_RATIO = 3

export type AnomalyReason =
  | 'not-finite'
  | 'not-positive'
  | 'absolute-threshold'
  | 'budget-ratio'

export interface AnomalyClassification {
  isAnomalous: boolean
  reason?: AnomalyReason
}

export interface TrustExclusionSummary {
  excludedCount: number
  excludedAmount: number
  trustedTotal: number
  hasExclusions: boolean
}

// Plain decimal notation only (optional sign, digits, optional fraction).
// Exponent forms ("1e21"), currency symbols, separators, and free text are all
// rejected → classified 'not-finite' (anomalous), NEVER silently coerced to 0.
const PLAIN_DECIMAL_RE = /^-?\d+(\.\d+)?$/

/**
 * PR-F4E: reduce an unknown stored amount to a finite JS number, or null.
 *
 *   • number        → returned if finite, else null
 *   • string        → trimmed; accepted only in plain decimal notation
 *   • Decimal-like  → read ONLY via a guarded toString() (mirrors the PR-F4B
 *     moneyResponse normalizer: never imports the Prisma Decimal type, never
 *     lets a throwing/exotic toString escape, rejects "[object Object]")
 *   • anything else → null
 *
 * null means "cannot be trusted as a money value" — callers classify it as
 * anomalous ('not-finite'), which EXCLUDES it from every financial aggregate.
 * Invalid input is therefore visible as an exclusion, never a fabricated $0.
 */
export function normalizeContractAmountInput(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!PLAIN_DECIMAL_RE.test(trimmed)) return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const toStr = (value as { toString?: unknown }).toString
    if (typeof toStr !== 'function') return null
    let str: unknown
    try {
      str = (toStr as () => unknown).call(value)
    } catch {
      return null
    }
    if (typeof str !== 'string' || str === '[object Object]') return null
    const trimmed = str.trim()
    if (!PLAIN_DECIMAL_RE.test(trimmed)) return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Classify a single contract amount.
 * Returns { isAnomalous: false } for amounts that are safe to include in
 * financial aggregates, or { isAnomalous: true, reason } for anomalous ones.
 *
 * PR-F4E: `amount` and `budgetTotal` both accept number | string | Decimal-like.
 * An invalid budgetTotal falls back to "no budget context" (absolute threshold
 * only) — identical to today's 0/missing budget behavior, never a crash.
 */
export function classifyContractAmount(
  amount: unknown,
  budgetTotal?: unknown,
): AnomalyClassification {
  const n = normalizeContractAmountInput(amount)
  if (n === null)
    return { isAnomalous: true, reason: 'not-finite' }
  if (n <= 0)
    return { isAnomalous: true, reason: 'not-positive' }
  if (n >= ANOMALY_ABSOLUTE_THRESHOLD)
    return { isAnomalous: true, reason: 'absolute-threshold' }
  const budget = normalizeContractAmountInput(budgetTotal)
  if (budget !== null && budget > 0 && n > budget * ANOMALY_BUDGET_RATIO)
    return { isAnomalous: true, reason: 'budget-ratio' }
  return { isAnomalous: false }
}

/** Convenience boolean — delegates to classifyContractAmount. */
export function isAnomalousContractAmount(amount: unknown, budgetTotal?: unknown): boolean {
  return classifyContractAmount(amount, budgetTotal).isAnomalous
}

/**
 * Partition a list of contracts into trusted and anomalous buckets.
 * Returns counts, excluded amount, and the trusted running total.
 *
 * PR-F4E: totals are accumulated from the NORMALIZED numeric value — a
 * Decimal-like totalAmount can never string-concatenate into the running sums.
 */
export function summarizeFinancialTrustExclusions(
  contracts: ReadonlyArray<{ totalAmount: unknown }>,
  budgetTotal?: unknown,
): TrustExclusionSummary {
  let excludedCount = 0
  let excludedAmount = 0
  let trustedTotal = 0
  for (const c of contracts) {
    const n = normalizeContractAmountInput(c.totalAmount)
    if (isAnomalousContractAmount(c.totalAmount, budgetTotal)) {
      excludedCount++
      if (n !== null && n > 0) {
        excludedAmount += n
      }
    } else {
      // Non-anomalous implies n !== null (classification would have rejected it).
      trustedTotal += n as number
    }
  }
  return {
    excludedCount,
    excludedAmount,
    trustedTotal,
    hasExclusions: excludedCount > 0,
  }
}
